<template>
  <HeaderComponent/>
  <div class=" py-[50px] pt-[130px] w-full flex items-center justify-center">
    <div class="container lg:max-w-[1024px] w-full px-[20px]">
      <div class="bg-white rounded-xl shadow-md overflow-hidden">
        <!-- En-tête -->
        <div class="bg-[#D4AF37] p-6 text-white">
          <h1 class="text-2xl font-bold">Réserver cet appartement</h1>
          <p class="mt-1">{{ apartment.title }}</p>
        </div>

        <!-- Formulaire -->
        <form @submit.prevent="handleReservation" class="p-6 space-y-6">
          <!-- Section Informations personnelles -->
          <div class="space-y-4">
            <h2 class="text-lg font-medium text-gray-900 border-b pb-2">Informations personnelles</h2>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nom complet*</label>
                <input
                  v-model="form.fullName"
                  required
                  placeholder="Jean Dupont"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="jean@exemple.com"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone*</label>
                <input
                  v-model="form.phone"
                  required
                  placeholder="+229 12 34 56 78"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  v-model="form.address"
                  placeholder="123 Rue des Cocotiers"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nationalité*</label>
                <input
                  v-model="form.nationality"
                  required
                  placeholder="Béninoise"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Type de pièce d'identité*</label>
                <select
                  v-model="form.identityType"
                  required
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                >
                  <option value="">Sélectionnez...</option>
                  <option value="passeport">Passeport</option>
                  <option value="cni">Carte Nationale d'Identité</option>
                  <option value="permis">Permis de conduire</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Numéro de pièce d'identité*</label>
              <input
                v-model="form.identityNumber"
                required
                placeholder="AB1234567"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
              />
            </div>
          </div>

          <!-- Section Dates de séjour -->
          <div class="space-y-4">
            <h2 class="text-lg font-medium text-gray-900 border-b pb-2">Dates de séjour</h2>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Date d'arrivée*</label>
                <input
                  v-model="form.startDate"
                  type="date"
                  required
                  :min="today"
                  @change="calculateTotalPrice"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Date de départ*</label>
                <input
                  v-model="form.endDate"
                  type="date"
                  required
                  :min="form.startDate || today"
                  @change="calculateTotalPrice"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                />
              </div>
            </div>

          <!-- Affichage des détails du prix -->
            <div v-if="form.startDate && form.endDate" class="bg-gray-50 p-4 rounded-lg">
              <div class="space-y-3">
                <div class="flex justify-between">
                  <p class="text-sm text-gray-500">Durée du séjour</p>
                  <p class="font-medium">{{ stayDuration }} nuit(s)</p>
                </div>
                
                <!-- Tarification court séjour (moins de 7 jours) - PAS de commission -->
                <template v-if="stayDuration < 7">
                  <div class="flex justify-between">
                    <p class="text-sm text-gray-500">{{ stayDuration }} nuit(s) × {{ formatPrice(pricePerNight) }} FCFA</p>
                    <p class="font-medium">{{ formatPrice(form.totalPrice) }} FCFA</p>
                  </div>
                </template>
                
                <!-- Tarification 7-29 jours -->
                <template v-else-if="stayDuration >= 7 && stayDuration < 30">
                  <!-- Si prix semaine défini - AVEC commission sur le forfait uniquement -->
                  <template v-if="hasWeeklyPrice">
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ Math.floor(stayDuration / 7) }} semaine(s) × {{ formatPrice(weeklyPrice) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice(Math.floor(stayDuration / 7) * weeklyPrice) }} FCFA</p>
                    </div>
                    <div class="flex justify-between text-green-600">
                      <p class="text-sm">Commission (10% sur forfait)</p>
                      <p class="font-medium">+ {{ formatPrice(Math.round(Math.floor(stayDuration / 7) * weeklyPrice * 0.10)) }} FCFA</p>
                    </div>
                    <div v-if="stayDuration % 7 > 0" class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ stayDuration % 7 }} nuit(s) supp. × {{ formatPrice(pricePerNight) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice((stayDuration % 7) * pricePerNight) }} FCFA</p>
                    </div>
                  </template>
                  <!-- Si prix semaine NON défini - SANS commission -->
                  <template v-else>
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ stayDuration }} nuit(s) × {{ formatPrice(pricePerNight) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice(form.totalPrice) }} FCFA</p>
                    </div>
                    <div class="text-xs text-gray-400 italic">
                      Prix semaine non défini - tarif nuitée appliqué
                    </div>
                  </template>
                </template>
                
                <!-- Tarification 30 jours -->
                <template v-else-if="stayDuration === 30">
                  <!-- Si prix mois défini - AVEC commission -->
                  <template v-if="hasMonthlyPrice">
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">Forfait mensuel (30 nuits)</p>
                      <p class="font-medium">{{ formatPrice(monthlyPrice) }} FCFA</p>
                    </div>
                    <div class="flex justify-between text-green-600">
                      <p class="text-sm">Commission (10%)</p>
                      <p class="font-medium">+ {{ formatPrice(Math.round(monthlyPrice * 0.10)) }} FCFA</p>
                    </div>
                  </template>
                  <!-- Si prix mois NON défini - SANS commission -->
                  <template v-else>
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ stayDuration }} nuit(s) × {{ formatPrice(pricePerNight) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice(form.totalPrice) }} FCFA</p>
                    </div>
                    <div class="text-xs text-gray-400 italic">
                      Prix mensuel non défini - tarif nuitée appliqué
                    </div>
                  </template>
                </template>
                
                <!-- Tarification plus de 30 jours -->
                <template v-else>
                  <!-- Si prix mois défini - AVEC commission sur le forfait uniquement -->
                  <template v-if="hasMonthlyPrice">
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">Forfait mensuel (30 nuits)</p>
                      <p class="font-medium">{{ formatPrice(monthlyPrice) }} FCFA</p>
                    </div>
                    <div class="flex justify-between text-green-600">
                      <p class="text-sm">Commission (10% sur forfait)</p>
                      <p class="font-medium">+ {{ formatPrice(Math.round(monthlyPrice * 0.10)) }} FCFA</p>
                    </div>
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ stayDuration - 30 }} nuit(s) supp. × {{ formatPrice(pricePerNight) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice((stayDuration - 30) * pricePerNight) }} FCFA</p>
                    </div>
                  </template>
                  <!-- Si prix mois NON défini - SANS commission -->
                  <template v-else>
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-500">{{ stayDuration }} nuit(s) × {{ formatPrice(pricePerNight) }} FCFA</p>
                      <p class="font-medium">{{ formatPrice(form.totalPrice) }} FCFA</p>
                    </div>
                    <div class="text-xs text-gray-400 italic">
                      Prix mensuel non défini - tarif nuitée appliqué
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </div>

          <!-- Section Paiement -->
          <div class="space-y-4">
            <h2 class="text-lg font-medium text-gray-900 border-b pb-2">Options de paiement</h2>
            
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex items-center justify-between mb-2">
                <span class="font-medium">Total:</span>
                <span class="font-bold text-lg text-[#D4AF37]">{{ formatPrice(form.totalPrice) }} FCFA</span>
              </div>
              
              <div class="space-y-3">

                <label class="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-100 transition">
                  <input 
                    type="radio" 
                    v-model="form.paymentChoice" 
                    value="half" 
                    class="h-4 w-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                  <div class="flex-1">
                    <span class="block font-medium">Payer 50% maintenant ({{ formatPrice(form.totalPrice / 2) }} FCFA)</span>
                    <span class="block text-sm text-gray-500">Solde à payer à l'arrivée</span>
                  </div>
                </label>

                <label class="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-100 transition">
                  <input 
                    type="radio" 
                    v-model="form.paymentChoice" 
                    value="full" 
                    class="h-4 w-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                  <div class="flex-1">
                    <span class="block font-medium">Payer 100% maintenant ({{ formatPrice(form.totalPrice) }} FCFA)</span>
                    <span class="block text-sm text-gray-500">Paiement complet sécurisé</span>
                  </div>
                </label>
              </div>
            </div>

            <div v-if="form.paymentChoice !== 'none'" class="mt-4 space-y-3">
              <h3 class="text-sm font-medium text-gray-700">Méthode de paiement</h3>
              <div class="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50">
                <input 
                  type="radio" 
                  v-model="form.paymentMethod" 
                  value="kkiapay" 
                  class="h-4 w-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                  checked
                />
                <span class="font-medium text-gray-700">KKiaPay</span>
                <div class="flex items-center space-x-2 ml-2">
                  <div class="w-[40px] h-[25px] rounded-[2px] flex items-center justify-center relative overflow-hidden">
                    <img src="@/assets/images/momo_mtn.png" alt="MTN MoMo">
                  </div>
                  <div class="w-[40px] h-[25px] rounded-[2px] flex items-center justify-center relative overflow-hidden">
                    <img src="@/assets/images/Moov_Africa_logo.png" alt="Moov Flooz">
                  </div>
                  <div class="w-[40px] h-[25px] rounded-[2px] flex items-center justify-center relative overflow-hidden">
                    <img src="@/assets/images/mastercard.png" alt="Mastercard">
                  </div>
                  <div class="w-[40px] h-[25px] rounded-[2px] flex items-center justify-center relative overflow-hidden">
                    <img src="@/assets/images/Visa_Inc._logo.svg.webp" alt="Visa">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Boutons -->
          <div class="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              @click="router.go(-1)"
              class="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="px-6 py-3 rounded-lg font-medium text-white bg-[#D4AF37] hover:bg-[#C9A227] transition-colors disabled:opacity-75"
            >
              <span v-if="isSubmitting">
                <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Traitement...
              </span>
              <span v-else>
                {{ form.paymentChoice === 'none' ? 'Confirmer la réservation' : 'Procéder au paiement' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <FooterComponent/>
</template>

<script setup>
import HeaderComponent from '@/components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '@/services/api'

const route = useRoute()
const router = useRouter()
const apartmentId = route.params.id

const today = computed(() => {
  const date = new Date()
  return date.toISOString().split('T')[0]
})

const loading = ref(true)
const apartment = ref({})
const isSubmitting = ref(false)

const form = ref({
  fullName: '',
  email: '',
  phone: '',
  address: '',
  nationality: '',
  identityType: '',
  identityNumber: '',
  apartmentId: apartmentId,
  startDate: today.value,
  endDate: '',
  totalPrice: 0,
  paymentChoice: 'none',
  paymentMethod: 'kkiapay',
})

watch(() => form.value.paymentChoice, (newVal) => {
  if (newVal === 'none') {
    form.value.paymentMethod = 'onarrival'
  } else if (form.value.paymentMethod === 'onarrival' || form.value.paymentMethod === 'none') {
    form.value.paymentMethod = 'kkiapay'
  }
})


// Calcul de la durée du séjour
const stayDuration = computed(() => {
  if (!form.value.startDate || !form.value.endDate) return 0
  
  const start = new Date(form.value.startDate)
  const end = new Date(form.value.endDate)
  const diffTime = end - start
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
})

// Prix par nuit (tarif court séjour pour affichage)
const pricePerNight = computed(() => {
  return apartment.value.shortStayPrice || apartment.value.price
})

// Prix hebdomadaire (7 jours) - retourne null si non défini
const weeklyPrice = computed(() => {
  return apartment.value.weeklyPrice || null
})

// Prix mensuel (30 jours) - retourne null si non défini
const monthlyPrice = computed(() => {
  return apartment.value.longStayPrice || null
})

// Vérifier si les prix spéciaux sont définis
const hasWeeklyPrice = computed(() => {
  return !!apartment.value.weeklyPrice
})

const hasMonthlyPrice = computed(() => {
  return !!apartment.value.longStayPrice
})

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price)
}

// Calcul du prix total selon la nouvelle logique avec commission
const calculateTotalPrice = () => {
  if (form.value.startDate && form.value.endDate) {
    const days = stayDuration.value
    const shortPrice = apartment.value.shortStayPrice || apartment.value.price
    const weekPrice = apartment.value.weeklyPrice // peut être undefined
    const longPrice = apartment.value.longStayPrice // peut être undefined
    
    let basePrice = 0
    
    if (days < 7) {
      // Moins de 7 jours : prix court séjour * nombre de jours (PAS de commission)
      basePrice = days * shortPrice
      form.value.totalPrice = basePrice
    } else if (days < 30) {
      // 7-29 jours
      if (weekPrice) {
        // Prix semaine défini : (semaines × weeklyPrice × 1.10) + (jours restants × shortStayPrice)
        // Commission 10% UNIQUEMENT sur le prix hebdomadaire
        const weeks = Math.floor(days / 7)
        const extraDays = days % 7
        const weeklyWithCommission = Math.round(weeks * weekPrice * 1.10)
        const extraNights = extraDays * shortPrice
        form.value.totalPrice = weeklyWithCommission + extraNights
      } else {
        // Prix semaine NON défini : prix nuitée * jours (SANS commission)
        basePrice = days * shortPrice
        form.value.totalPrice = basePrice
      }
    } else if (days === 30) {
      // 30 jours exactement
      if (longPrice) {
        // Prix mensuel défini (AVEC 10% commission)
        basePrice = longPrice
        form.value.totalPrice = Math.round(basePrice * 1.10)
      } else {
        // Prix mensuel NON défini : prix nuitée * 30 (SANS commission)
        basePrice = days * shortPrice
        form.value.totalPrice = basePrice
      }
    } else {
      // Plus de 30 jours
      if (longPrice) {
        // Prix mensuel défini : (longStayPrice × 1.10) + (jours supp. × shortStayPrice)
        // Commission 10% UNIQUEMENT sur le prix mensuel
        const extraDays = days - 30
        const monthlyWithCommission = Math.round(longPrice * 1.10)
        const extraNights = extraDays * shortPrice
        form.value.totalPrice = monthlyWithCommission + extraNights
      } else {
        // Prix mensuel NON défini : prix nuitée * jours (SANS commission)
        basePrice = days * shortPrice
        form.value.totalPrice = basePrice
      }
    }
  }
}

// Récupération des infos de l'appartement
const fetchApartment = async () => {
  try {
    const { data } = await apiClient.get(`/apartments/${apartmentId}`)
    apartment.value = data
    calculateTotalPrice() // Calcul initial
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'appartement:', error)
    router.push('/')
  } finally {
    loading.value = false
  }
}

const handleReservation = async () => {
  isSubmitting.value = true;
  
  try {
    // Validation des champs obligatoires
    if (!form.value.fullName || !form.value.email || !form.value.phone || 
        !form.value.startDate || !form.value.endDate) {
      throw new Error('Veuillez remplir tous les champs obligatoires');
    }

    // Validation de la date de fin
    if (new Date(form.value.endDate) <= new Date(form.value.startDate)) {
      throw new Error('La date de fin doit être après la date de début');
    }

    // Validation supplémentaire pour paiement en ligne
    if (form.value.paymentChoice !== 'none' && (!form.value.paymentMethod || form.value.paymentMethod === 'none')) {
      throw new Error('Veuillez sélectionner une méthode de paiement');
    }

    // Calculer le montant à payer selon le choix
    let paidAmount = 0;
    if (form.value.paymentChoice === 'half') {
      paidAmount = Math.round(form.value.totalPrice / 2);
    } else if (form.value.paymentChoice === 'full') {
      paidAmount = form.value.totalPrice;
    }

    const payload = {
      ...form.value,
      // Pour paiement sur place, on envoie 'onarrival' comme paymentMethod
      paymentMethod: form.value.paymentChoice === 'none' ? 'onarrival' : form.value.paymentMethod,
      paidAmount: paidAmount,
      stayDuration: stayDuration.value,
      pricePerNight: pricePerNight.value
    };

    console.log('Payload envoyé:', payload);

    const { data } = await apiClient.post('/reservations/initiate', payload);
    
    console.log('Réponse reçue:', data);

    // Si paiement sur place
    if (form.value.paymentChoice === 'none') {
      router.push({
        name: 'ReservationConfirmation',
        query: { 
          paymentChoice: 'none'
        }
      });
    } else {
      // Ouvrir le widget KKiaPay
      if (data.paymentConfig && typeof window.openKkiapayWidget === 'function') {
        // Stocker l'ID de réservation pour le success listener
        window._kkiapayReservationId = data.reservationId;
        window.openKkiapayWidget(data.paymentConfig);
      } else {
        throw new Error('Widget KKiaPay non disponible ou configuration manquante');
      }
    }
  } catch (error) {
    console.error('Erreur de réservation:', error);
    alert(error.response?.data?.message || error.message || 'Une erreur est survenue. Veuillez réessayer.');
  } finally {
    isSubmitting.value = false;
  }
};

// Charger le script KKiaPay et configurer les listeners
const setupKkiapay = () => {
  // Charger le SDK KKiaPay si pas déjà présent
  if (!document.querySelector('script[src*="cdn.kkiapay.me"]')) {
    const script = document.createElement('script');
    script.src = 'https://cdn.kkiapay.me/k.js';
    script.async = true;
    document.head.appendChild(script);
  }

  // Configurer le listener de succès une fois le SDK prêt
  const attachListeners = () => {
    if (typeof window.addSuccessListener === 'function') {
      window.addSuccessListener(async ({ transactionId }) => {
        const reservationId = window._kkiapayReservationId;
        try {
          const { data } = await apiClient.get('/reservations/verify', {
            params: { transactionId, reservationId }
          });
          router.push({
            name: 'ReservationConfirmation',
            query: {
              status: data.status,
              reservationId: data.id,
              transactionId
            }
          });
        } catch (err) {
          console.error('Erreur vérification KKiaPay:', err);
          alert('Paiement reçu mais une erreur est survenue lors de la vérification. Contactez le support.');
        }
      });

      window.addFailedListener(({ failureCode, failureMessage }) => {
        console.error('Paiement KKiaPay échoué:', failureCode, failureMessage);
        alert(`Paiement échoué: ${failureMessage || 'Veuillez réessayer.'}`);
      });
    } else {
      setTimeout(attachListeners, 500);
    }
  };

  attachListeners();
};

// Au montage du composant
onMounted(() => {
  fetchApartment();
  setupKkiapay();
})
</script>

<style scoped>
/* Animation de chargement */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Amélioration visuelle */
.bg-gray-50 {
  background-color: #f9fafb;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.transition {
  transition: all 0.2s ease;
}

/* Style pour les boutons */
button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

/* Style pour les cartes de paiement */
.payment-card {
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.payment-card:hover {
  border-color: #D4AF37;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.payment-card.selected {
  border-color: #D4AF37;
  background-color: rgba(212, 175, 55, 0.05);
}
</style>